// Shared between investment-account.jsx (the index/card list) and both of its
// drill-downs (investment-portfolio-activity.jsx, investment-unlinked-activity.jsx)
// -- all three need the exact same "how many cards' worth of investment
// activity does this donor have" count: the index uses it to decide whether
// to redirect straight into the sole card instead of showing a list of one,
// and each drill-down uses it to decide whether a Back arrow (which would
// just bounce back into that same redirect) makes sense to show at all.
//
// FLIGHT-299: a Contribution can now post directly to one specific
// portfolio (previously it could only ever link to a Fund, never a
// portfolio), so only a portfolioId-less Contribution counts as "unlinked"
// here -- same "attributable vs. not" split balanceAdjustments already uses.
export function hasUnlinkedInvestmentActivity(fund) {
  return (
    (fund?.contributions || []).some((c) => c.walletType === 'investment' && !c.portfolioId) ||
    (fund?.balanceAdjustments || []).some((ba) => ba.walletType === 'investment' && !ba.portfolioId)
  )
}

// BalanceAdjustment.createdAt is USUALLY a genuine "posted right now" moment
// (correctly shown in the viewer's local timezone), but some are
// deliberately backdated to match a calendar date elsewhere on the same
// record -- an Investment Value Adjustment's reconciliation entry is backdated
// to its statementDate, an Investment Portfolio's opening-balance entry to its
// effectiveDate (see investment-value-adjustments.service.ts / investment-
// portfolios.service.ts) -- both always constructed as UTC midnight from a
// plain <input type="date">, same as statementDate/effectiveDate/
// lastStatementDate elsewhere in this codebase. Formatting one of those in
// local time can roll it back a day for anyone west of UTC, exactly the bug
// already fixed for the Investment Value Adjustment row above it.
// A genuine "right now" timestamp landing on exact UTC midnight is
// astronomically unlikely, so that's a safe, general signal to tell the two
// apart without needing a new backend field.
export function isCalendarDateOnly(value) {
  if (!value) return false
  const date = new Date(value)
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  )
}

// Re-expresses a UTC calendar-date value as the equivalent local-midnight
// Date, so a plain local-timezone formatter (toDateStringNumeric) renders the
// same calendar day everywhere instead of rolling it back a day west of UTC.
export function toLocalMidnight(value) {
  const date = new Date(value)
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

// FLIGHT-295 follow-up: an Investment Transfer shows as its own activity row
// on BOTH the Giving Wallet's activity view and the Investment Portfolio's
// own activity view, from the moment it's Requested -- reversing this page's
// earlier "don't render transfers separately" decision (see the comment
// above balanceAdjustmentRows in investment-portfolio-activity.jsx). These
// helpers are deliberately self-contained here rather than reusing the
// admin-only formatEndpoint.js/constants.js in management/investment-
// transfers/ -- donor-facing code shouldn't depend on admin-management code,
// even for a pure display-string helper.
// 'outgoing' if this side (a portfolioId, or a fundId when the transfer's
// Giving-side endpoint) is the FROM side, 'incoming' if the TO side, null if
// this transfer doesn't touch this side at all (shouldn't happen given how
// callers scope the fetch, but a transfer could in principle be
// Investment<->Investment between two portfolios of the same fund, which
// never touches fromFundId/toFundId -- see resolveTransferIdentity.js's own
// comment on this in the admin feature).
export function investmentTransferDirection(transfer, { portfolioId, fundId } = {}) {
  if (portfolioId != null) {
    if (transfer.fromPortfolioId === portfolioId) return 'outgoing'
    if (transfer.toPortfolioId === portfolioId) return 'incoming'
  }
  if (fundId != null) {
    if (transfer.fromWalletType === 'GIVING' && transfer.fromFundId === fundId) return 'outgoing'
    if (transfer.toWalletType === 'GIVING' && transfer.toFundId === fundId) return 'incoming'
  }
  return null
}

// <ACCOUNT NUMBER> (<FIRM>) for whichever Investment Account is on the given
// side ('from'/'to') of the transfer -- the firm part drops (with its
// parentheses) when the portfolio has no firm set. Only the side actually
// walletType INVESTMENT resolves to a non-null portfolio. Mirrors
// api/src/onboarding/flow-definitions/investment-transfer.flow.ts's own
// investmentAccountNote() helper. Deliberately without the advisor, unlike
// formatInvestmentAccountLabel -- these notes read as a short sentence.
function investmentSideNote(transfer, side) {
  const portfolio = side === 'from' ? transfer.fromPortfolio : transfer.toPortfolio
  if (!portfolio) return 'Investment Account'
  const firm = portfolio.financialFirm?.name
  return firm ? `${portfolio.custodialNumber} (${firm})` : portfolio.custodialNumber
}

// Title-cased so Icons.State (which only capitalizes the first character)
// renders "In Transit" correctly rather than the raw "in_transit". Kept
// local rather than importing management/investment-transfers/constants.js's
// STEP_LABELS -- donor-facing code shouldn't depend on admin-management
// code, same rule as this file's other helpers.
const STEP_STATE_LABEL = {
  requested: 'Requested',
  approved_pending: 'Approved: Pending',
  approved: 'Approved',
  in_transit: 'In Transit',
  // FLIGHT-295 follow-up: posted_pending_accounting is an internal admin-only
  // step (see investment-transfer.flow.ts) -- the donor sees no difference
  // from the true final `complete` step, since the money already moved and
  // they were already notified by the time a transfer reaches either one.
  posted_pending_accounting: 'Posted',
  complete: 'Posted',
  cancelled: 'Cancelled',
}
export function investmentTransferStateLabel(transfer) {
  return STEP_STATE_LABEL[transfer.currentStep] || transfer.currentStep
}

// Mirrors the exact wording of the BalanceAdjustment descriptions the flow
// definition itself posts (minus the "Investment Transfer #N --" id prefix,
// which doesn't belong on a donor-facing row) so a transfer's row reads the
// same on both sides of the movement. The Giving Wallet's row names the
// counterpart Investment Account ("<ACCOUNT NUMBER> (<FIRM>)"); the
// Investment Account's own row just says where the money went. Text is the
// same regardless of currentStep -- this doesn't distinguish Requested from
// Posted; the row's own presence (and, once posted, the suppressed ledger row
// underneath it) already carries that.
export function investmentTransferDescription(transfer, direction) {
  const outgoing = direction === 'outgoing'
  const thisWalletType = outgoing ? transfer.fromWalletType : transfer.toWalletType
  const otherWalletType = outgoing ? transfer.toWalletType : transfer.fromWalletType

  if (thisWalletType === 'GIVING') {
    return outgoing
      ? `Funds Transfer to Investment Account: ${investmentSideNote(transfer, 'to')}`
      : `Funds Transfer From Investment Account: ${investmentSideNote(transfer, 'from')}`
  }

  return outgoing && otherWalletType === 'GIVING'
    ? 'Funds Transfer to Giving Wallet'
    : 'Funds Transfer to Investment Account'
}

// The stage date matching the transfer's current step (Requested -> its own
// dateRequested, Approved -> dateApproved, etc), or updatedAt once
// cancelled, since InvestmentTransferDto has no dateCancelled column and
// updatedAt is the best available signal for "when did this reach its final
// state." Stage dates on an ADMIN-created (possibly backdated) transfer are
// genuinely calendar-only -- re-expressed as local midnight, same
// isCalendarDateOnly guard already used for BalanceAdjustment.createdAt
// above -- but a DONOR-submitted transfer's dateRequested is always set to
// `new Date()` at the exact moment of submission, a real timestamp whose
// time-of-day matters for sorting the row among same-day activity; without
// this guard it collapsed straight to midnight regardless, sorting a
// just-submitted request behind anything else from later that same day.
const TRANSFER_STAGE_DATE_FIELD = {
  requested: 'dateRequested',
  approved: 'dateApproved',
  in_transit: 'dateInTransit',
  // FLIGHT-295 follow-up: datePosted stamps when the transfer enters
  // posted_pending_accounting (see investment-transfer.flow.ts's mark_posted
  // action) -- same field serves both this step and complete, matching
  // STEP_STATE_LABEL treating them identically for donor-facing purposes.
  posted_pending_accounting: 'datePosted',
  complete: 'datePosted',
}
export function investmentTransferRowDate(transfer) {
  const field = TRANSFER_STAGE_DATE_FIELD[transfer.currentStep]
  const value = field && transfer[field]
  if (!value) return transfer.updatedAt
  return isCalendarDateOnly(value) ? toLocalMidnight(value) : value
}

// FLIGHT-296 follow-up: the Description column was clipping to one line --
// see the comment on rowHeight in investment-portfolio-activity.jsx for why
// this grid can't use MUI X's own getRowHeight: 'auto' to grow rows to fit
// their content. Instead, the Description column alone is allowed to wrap
// (its renderCell), and this estimates how many lines that wrapped text will
// take at the column's own actual rendered width, so the caller can grow
// just that row by the extra line count -- every other column stays
// vertically centered in the resulting taller row for free via the grid's
// own flex layout.
const DESCRIPTION_CELL_FONT = '400 14px Montserrat, "Helvetica Neue", Arial, sans-serif'

let measureContext
function measureTextWidth(text) {
  if (typeof document === 'undefined') return 0
  if (!measureContext) measureContext = document.createElement('canvas').getContext('2d')
  measureContext.font = DESCRIPTION_CELL_FONT
  return measureContext.measureText(text).width
}

// Greedy word-wrap simulation matching how `white-space: normal` text lays
// out in a browser: never breaks mid-word, only starts a new line once the
// current one can no longer fit the next word. This only needs to decide
// "how many lines," not reproduce the browser's line breaking pixel-for-pixel.
export function wrappedLineCount(text, maxWidth) {
  if (!text || !maxWidth || maxWidth <= 0) return 1

  const { lines } = text.split(' ').reduce(
    ({ lines: lineCount, currentLine }, word) => {
      const candidate = currentLine ? `${currentLine} ${word}` : word
      if (currentLine && measureTextWidth(candidate) > maxWidth) {
        return { lines: lineCount + 1, currentLine: word }
      }
      return { lines: lineCount, currentLine: candidate }
    },
    { lines: 1, currentLine: '' }
  )
  return lines
}
